import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  IconButton,
  Box,
  RadioGroup,
  FormControlLabel,
  Radio,
  CircularProgress,
  Alert,
  TextField,
  MenuItem,
} from "@mui/material";
import { Close, CallMerge, Add } from "@mui/icons-material";
import { useSelector } from "react-redux";
import { useSnackbar } from "notistack";

import consolidationService from "../../services/consolidationService";
import { selectKeyForRole } from "../../redux/slices/statusDefinitionsSlice";

const NEW = "__new__";

// Shown whenever a user or admin adds a shipment (or one of its items) to a
// consolidation — never a silent auto-merge. Lists the shipment's OWN
// customer's open consolidations (an admin acting on a shipment only ever
// sees — and can only target — that shipment's customer's consolidations,
// never a different customer's, so consolidations can't be merged across
// customers) plus a "Create New Consolidation" option.
//
// `mode` is "shipment" (whole shipment via requestForShipment) or "item"
// (single item via addItem, requires `itemId`).
const ConsolidationPickerDialog = ({
  open,
  onClose,
  mode,
  shipmentId,
  customerId,
  itemId,
  onDone,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const completedKey = useSelector(selectKeyForRole("consolidation", "completed"));
  const cancelledKey = useSelector(selectKeyForRole("consolidation", "cancelled"));

  const [consolidations, setConsolidations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [choice, setChoice] = useState(NEW);
  const [holdPreference, setHoldPreference] = useState("ship_when_fulfilled");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    setChoice(NEW);
    setHoldPreference("ship_when_fulfilled");
    setLoading(true);
    consolidationService
      .getAll({ customer: customerId })
      .then(({ data }) => setConsolidations(data.data.consolidations || []))
      .catch(() => enqueueSnackbar("Failed to load consolidations", { variant: "error" }))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, customerId]);

  const openConsolidations = consolidations.filter(
    (c) => c.status !== completedKey && c.status !== cancelledKey,
  );

  const selectedConsolidation =
    choice !== NEW ? openConsolidations.find((c) => c._id === choice) : null;

  const handleChoiceChange = (e) => {
    const value = e.target.value;
    setChoice(value);
    const picked = openConsolidations.find((c) => c._id === value);
    setHoldPreference(picked?.holdPreference || "ship_when_fulfilled");
  };

  const handleConfirm = async () => {
    const consolidationId = choice === NEW ? undefined : choice;
    setSubmitting(true);
    try {
      const { data } =
        mode === "item"
          ? await consolidationService.addItem(shipmentId, itemId, consolidationId)
          : await consolidationService.requestForShipment(shipmentId, consolidationId);

      const targetId = consolidationId || data.data.consolidation?._id;
      if (targetId && holdPreference !== selectedConsolidation?.holdPreference) {
        await consolidationService.updatePreference(targetId, holdPreference);
      }

      enqueueSnackbar(data.message, { variant: "success" });
      onDone?.(data.data.consolidation);
      onClose();
    } catch (err) {
      enqueueSnackbar(err.response?.data?.message || "Failed to update consolidation", { variant: "error" });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
      <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Typography fontWeight={700}>
          {mode === "item" ? "Add Item to Consolidation" : "Consolidate Shipment"}
        </Typography>
        <IconButton size="small" onClick={onClose}>
          <Close fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ pt: 2.5 }}>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
            <CircularProgress size={26} />
          </Box>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            <Alert severity="info" sx={{ borderRadius: 2 }}>
              {mode === "item"
                ? "Add this item to one of your existing open consolidations, or start a new one."
                : "Add this shipment's items to one of your existing open consolidations, or start a new one."}
            </Alert>

            <RadioGroup value={choice} onChange={handleChoiceChange}>
              <FormControlLabel
                value={NEW}
                control={<Radio size="small" />}
                label={
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Add sx={{ fontSize: 16 }} />
                    <Typography variant="body2" fontWeight={600}>
                      Create New Consolidation
                    </Typography>
                  </Box>
                }
              />

              {openConsolidations.length > 0 && (
                <Box sx={{ mt: 1, mb: 0.5 }}>
                  <Typography variant="caption" color="text.secondary">
                    Or add to an existing one
                  </Typography>
                </Box>
              )}

              {openConsolidations.map((c) => (
                <Box key={c._id}>
                  <FormControlLabel
                    value={c._id}
                    sx={{ alignItems: "flex-start", mt: 0.5 }}
                    control={<Radio size="small" sx={{ mt: 0.25 }} />}
                    label={
                      <Box>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <CallMerge sx={{ fontSize: 16 }} />
                          <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
                            {c.consolidationNumber}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            ({c.items.length} item{c.items.length !== 1 ? "s" : ""})
                          </Typography>
                        </Box>
                        <Typography variant="caption" color="text.secondary" sx={{ pl: 3 }}>
                          {c.customer?.firstName} {c.customer?.lastName}
                        </Typography>
                      </Box>
                    }
                  />
                  {choice === c._id && (
                    <Box sx={{ pl: 4, pr: 1, pb: 1, mt: 2 }}>
                      <TextField
                        select
                        size="small"
                        fullWidth
                        label="What would you like to do?"
                        value={holdPreference}
                        onChange={(e) => setHoldPreference(e.target.value)}
                      >
                        <MenuItem value="ship_when_fulfilled">Ship once every item has been received</MenuItem>
                        <MenuItem value="hold_for_more">Hold for further shipments</MenuItem>
                      </TextField>
                      {holdPreference === "hold_for_more" && (
                        <Typography variant="caption" color="text.secondary" display="block" mt={0.75}>
                          Maximum holding time is 14 days — if no action is taken within this period, it will be
                          automatically sailed.
                        </Typography>
                      )}
                    </Box>
                  )}
                </Box>
              ))}
            </RadioGroup>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} variant="outlined">
          Cancel
        </Button>
        <Button onClick={handleConfirm} variant="contained" disabled={loading || submitting}>
          {submitting ? <CircularProgress size={18} color="inherit" /> : "Confirm"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ConsolidationPickerDialog;
